import { DateTime } from 'luxon';
import { resolveEffectiveTimeZone, resolveEffectiveZoneLabel } from './timezoneDisplay';

export interface OperatingSlot {
    localDate: string;
    localTime: string;
    startUtc: string;
    offsetMinutes: number;
    zoneLabel: string;
    displayLabel: string;
    ambiguous: boolean;
}

export function generateOperatingSlots(
    dateISO: string,
    tz: string,
    openTime: string,
    closeTime: string,
    slotMinutes: number = 60
): OperatingSlot[] {
    const [openH, openM] = openTime.split(':').map(Number);
    const [closeH, closeM] = closeTime.split(':').map(Number);
    const openTotal = openH * 60 + openM;
    const closeTotal = closeH * 60 + closeM;

    if (openTotal === closeTotal) return [];

    const [year, month, day] = dateISO.split('-').map(Number);
    const effectiveTz = resolveEffectiveTimeZone(tz, dateISO) || tz;
    let startDt = DateTime.fromObject({ year, month, day, hour: openH, minute: openM }, { zone: effectiveTz });
    let endDt = DateTime.fromObject({ year, month, day, hour: closeH, minute: closeM }, { zone: effectiveTz });

    if (closeTotal <= openTotal) {
        endDt = endDt.plus({ days: 1 });
    }

    const slots: OperatingSlot[] = [];
    let current = startDt;
    
    while (current < endDt) {
        const zoneLabel = resolveEffectiveZoneLabel(
            tz,
            current.toMillis(),
            current.offset,
            current.toFormat('ZZZZ')
        );
        slots.push({
            localDate: current.toFormat('yyyy-MM-dd'),
            localTime: current.toFormat('HH:mm'),
            startUtc: current.toUTC().toISO()!,
            offsetMinutes: current.offset,
            zoneLabel,
            displayLabel: '',
            ambiguous: false
        });
        current = current.plus({ minutes: slotMinutes });
    }
    
    const timeCounts = new Map<string, number>();
    for (const slot of slots) {
        const key = `${slot.localDate}T${slot.localTime}`;
        timeCounts.set(key, (timeCounts.get(key) || 0) + 1);
    }
    
    for (const slot of slots) {
        const key = `${slot.localDate}T${slot.localTime}`;
        const isAmbiguous = (timeCounts.get(key) || 0) > 1;
        slot.ambiguous = isAmbiguous;
        
        const dt = DateTime.fromISO(slot.startUtc).setZone(effectiveTz);
        const baseLabel = dt.toFormat('h:mm a');
        slot.displayLabel = isAmbiguous ? `${baseLabel} ${slot.zoneLabel}` : baseLabel;
    }

    return slots;
}

export function isOvernightWindow(openTime: string, closeTime: string): boolean {
    return closeTime < openTime;
}

// Fallback is no longer just strings, but keeping an empty array is safer if missing warehouse.
export const DEFAULT_REVISE_SLOTS: OperatingSlot[] = [];
